import * as i0 from '@angular/core';
import { EventEmitter, Component, ViewEncapsulation, Input, Output, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@pv-frontend/pv-shared-components/svg-icon';
import { SvgIconModule } from '@pv-frontend/pv-shared-components/svg-icon';

const CATEGORY_ICON_POSITION = { START: 'start', END: 'end' };
class ChipComponent {
    constructor(elementRef, renderer) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.classMappingPerVariant = {
            outlined: `pv-chip-outlined`,
            filled: `pv-chip-filled surface-border-normal pv-border-solid`,
        };
        this.classMappingPerSize = {
            small: 'small text-sm',
            normal: 'normal text-base',
            large: 'large text-lg',
        };
        this.CATEGORY_ICON_POSITION = CATEGORY_ICON_POSITION;
        this.isChipClickable = false;
        this.variant = 'filled';
        this.label = '';
        this.style = {};
        this.isRounded = true;
        this.size = 'normal';
        /**
         * Applies active behaviour to the item which is selected
         */
        this.isActive = false;
        this.showCrossIcon = false;
        /**
         * This is dynamically typed based upon the meta information input provided above
         *
         * Emits the `metaInformation` input when a clickable chip is clicked upon
         */
        this.clickEmitter = new EventEmitter();
        this.crossClickEmitter = new EventEmitter();
    }
    /**
     * Determines if the element should listen to clicks and show cursor when hovered upon
     */
    set isClickable(shouldSetupListener) {
        if (shouldSetupListener) {
            this.setupListener();
        }
        else {
            this.clearListener();
        }
    }
    get isClickable() {
        return this.isChipClickable;
    }
    handleCrossClick(event) {
        // using StopPropagation to avoid click event emitted by the parent div that would select the chip again
        event.stopPropagation();
        this.crossClickEmitter.emit();
    }
    setupListener() {
        this.isChipClickable = true;
        this.clickListener = this.renderer.listen(this.elementRef.nativeElement, 'click', () => {
            this.clickEmitter?.emit(this.metaInformation);
        });
    }
    clearListener() {
        this.isChipClickable = false;
        this.clickListener?.();
    }
    ngOnDestroy() {
        this.clearListener();
    }
}
ChipComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: ChipComponent, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
ChipComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.9", type: ChipComponent, selector: "pv-chip", inputs: { variant: "variant", label: "label", style: "style", isRounded: "isRounded", size: "size", isActive: "isActive", metaInformation: "metaInformation", categoryIcon: "categoryIcon", showCrossIcon: "showCrossIcon", crossIcon: "crossIcon", isClickable: "isClickable" }, outputs: { clickEmitter: "clickEmitter", crossClickEmitter: "crossClickEmitter" }, ngImport: i0, template: "<div\n  class=\"pv-chip-container surface-background-level1-low-contrast surface-text-subdued-low-contrast flex align-items-center justify-content-between gap-1\"\n  [ngClass]=\"[\n    this.classMappingPerVariant[this.variant],\n    this.classMappingPerSize[this.size]\n  ]\"\n  [class.is-rounded]=\"this.isRounded\"\n  [class.active]=\"this.isActive\"\n  [class.cursor-pointer]=\"this.isClickable\"\n  [style]=\"this.style?.container\">\n  <div\n    class=\"flex align-items-center gap-1 category-icon\"\n    [ngClass]=\"\n      this.categoryIcon?.position === CATEGORY_ICON_POSITION.END\n        ? 'flex-row-reverse'\n        : 'flex-row'\n    \">\n    <pv-svg-icon\n      *ngIf=\"this.categoryIcon\"\n      [srcUrl]=\"this.categoryIcon.url\"\n      styleClass=\"flex h-1rem w-1rem\"\n      [style]=\"this.style?.categoryIcon\" />\n    <span class=\"pv-chip-label\" [style]=\"this.style?.label\">\n      {{ this.label }}\n    </span>\n  </div>\n  <pv-svg-icon\n    *ngIf=\"this.isActive && this.showCrossIcon\"\n    [name]=\"this.crossIcon?.name ?? 'cross'\"\n    styleClass=\"flex h-1rem w-1rem cross-icon ml-2\"\n    [style]=\"this.style?.crossIcon\"\n    (click)=\"handleCrossClick($event)\" />\n</div>\n", styles: [".pv-chip-container.normal{padding:9px 20px}.pv-chip-container.small{padding:5px 15px;font-size:.875rem}.pv-chip-container.small .cross-icon{height:.875rem;width:.875rem}.pv-chip-container.large{padding:14px 25px;font-size:1.125rem}.pv-chip-container.large .cross-icon{height:1.125rem;width:1.125rem}.pv-chip-container.is-rounded{border-radius:50px}.pv-chip-container.pv-chip-filled.active{background-color:#fff;border-color:var(--primary-color);color:var(--primary-color);font-weight:700;border-width:thin;border-style:solid}.pv-chip-container.pv-chip-filled.active .cross-icon{stroke:var(--primary-color)}.pv-chip-container.pv-chip-outlined.active{background-color:var(--primary-color);color:var(--primary-color-text);font-weight:700}.pv-chip-container.pv-chip-outlined.active .cross-icon{stroke:var(--primary-color-text)}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i2.SvgIconComponent, selector: "pv-svg-icon", inputs: ["name", "srcUrl", "style", "styleClass"] }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: ChipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'pv-chip', encapsulation: ViewEncapsulation.None, template: "<div\n  class=\"pv-chip-container surface-background-level1-low-contrast surface-text-subdued-low-contrast flex align-items-center justify-content-between gap-1\"\n  [ngClass]=\"[\n    this.classMappingPerVariant[this.variant],\n    this.classMappingPerSize[this.size]\n  ]\"\n  [class.is-rounded]=\"this.isRounded\"\n  [class.active]=\"this.isActive\"\n  [class.cursor-pointer]=\"this.isClickable\"\n  [style]=\"this.style?.container\">\n  <div\n    class=\"flex align-items-center gap-1 category-icon\"\n    [ngClass]=\"\n      this.categoryIcon?.position === CATEGORY_ICON_POSITION.END\n        ? 'flex-row-reverse'\n        : 'flex-row'\n    \">\n    <pv-svg-icon\n      *ngIf=\"this.categoryIcon\"\n      [srcUrl]=\"this.categoryIcon.url\"\n      styleClass=\"flex h-1rem w-1rem\"\n      [style]=\"this.style?.categoryIcon\" />\n    <span class=\"pv-chip-label\" [style]=\"this.style?.label\">\n      {{ this.label }}\n    </span>\n  </div>\n  <pv-svg-icon\n    *ngIf=\"this.isActive && this.showCrossIcon\"\n    [name]=\"this.crossIcon?.name ?? 'cross'\"\n    styleClass=\"flex h-1rem w-1rem cross-icon ml-2\"\n    [style]=\"this.style?.crossIcon\"\n    (click)=\"handleCrossClick($event)\" />\n</div>\n", styles: [".pv-chip-container.normal{padding:9px 20px}.pv-chip-container.small{padding:5px 15px;font-size:.875rem}.pv-chip-container.small .cross-icon{height:.875rem;width:.875rem}.pv-chip-container.large{padding:14px 25px;font-size:1.125rem}.pv-chip-container.large .cross-icon{height:1.125rem;width:1.125rem}.pv-chip-container.is-rounded{border-radius:50px}.pv-chip-container.pv-chip-filled.active{background-color:#fff;border-color:var(--primary-color);color:var(--primary-color);font-weight:700;border-width:thin;border-style:solid}.pv-chip-container.pv-chip-filled.active .cross-icon{stroke:var(--primary-color)}.pv-chip-container.pv-chip-outlined.active{background-color:var(--primary-color);color:var(--primary-color-text);font-weight:700}.pv-chip-container.pv-chip-outlined.active .cross-icon{stroke:var(--primary-color-text)}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { variant: [{
                type: Input
            }], label: [{
                type: Input
            }], style: [{
                type: Input
            }], isRounded: [{
                type: Input
            }], size: [{
                type: Input
            }], isActive: [{
                type: Input
            }], metaInformation: [{
                type: Input
            }], categoryIcon: [{
                type: Input
            }], showCrossIcon: [{
                type: Input
            }], crossIcon: [{
                type: Input
            }], clickEmitter: [{
                type: Output
            }], crossClickEmitter: [{
                type: Output
            }], isClickable: [{
                type: Input
            }] } });

class PvChipModule {
}
PvChipModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PvChipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PvChipModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.9", ngImport: i0, type: PvChipModule, declarations: [ChipComponent], imports: [CommonModule, SvgIconModule], exports: [ChipComponent] });
PvChipModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PvChipModule, imports: [CommonModule, SvgIconModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PvChipModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SvgIconModule],
                    exports: [ChipComponent],
                    declarations: [ChipComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CATEGORY_ICON_POSITION, ChipComponent, PvChipModule };
